import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTenantMe } from "@/lib/tenant-me";
import { useUserMe } from "@/lib/user-me";
import { useUpdatePreferences } from "@/lib/auth-me";
import { useToast } from "@/components/v2/Toast";
import { SettingsSection } from "@/components/v2/account/SettingsSection";

const LOCALE_OPTIONS = [
  { value: "en-PH", label: "English (Philippines)" },
  { value: "en-US", label: "English (US)" },
  { value: "en-GB", label: "English (UK)" },
  { value: "en-AU", label: "English (Australia)" },
  { value: "en-SG", label: "English (Singapore)" },
];

const TIMEZONE_OPTIONS = [
  "Asia/Manila",
  "Asia/Jakarta",
  "Asia/Singapore",
  "Asia/Kuala_Lumpur",
  "Asia/Tokyo",
  "Asia/Hong_Kong",
  "America/Los_Angeles",
  "America/New_York",
  "Europe/London",
  "Europe/Berlin",
  "Australia/Sydney",
  "Pacific/Auckland",
];

/**
 * `/account/preferences` — per-user locale + timezone. Empty timezone
 * inherits the workspace default. Locale defaults to en-PH so we
 * always have a sensible value (the API stores null and the UI
 * surfaces that as the en-PH default to keep the form simple).
 */
export function AccountPreferences() {
  const me = useUserMe();
  const tenant = useTenantMe();
  const update = useUpdatePreferences();
  const toast = useToast();

  const currentLocale = me.data?.locale ?? null;
  const currentTimezone = me.data?.timezone ?? null;
  const workspaceTimezone = tenant.data?.tenant?.timezone ?? "Asia/Manila";

  const [locale, setLocale] = useState(currentLocale ?? "en-PH");
  const [timezone, setTimezone] = useState(currentTimezone ?? "");

  useEffect(() => {
    setLocale(currentLocale ?? "en-PH");
    setTimezone(currentTimezone ?? "");
  }, [currentLocale, currentTimezone]);

  const dirty =
    locale !== (currentLocale ?? "en-PH") ||
    (timezone || null) !== currentTimezone;

  return (
    <SettingsSection
      title="Preferences"
      description="Personal display preferences. Empty timezone falls back to the workspace default."
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="locale" className="text-[13px] font-semibold">
            Language / locale
          </Label>
          <Select value={locale} onValueChange={setLocale}>
            <SelectTrigger id="locale">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LOCALE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="timezone" className="text-[13px] font-semibold">
            Timezone
          </Label>
          <Select
            value={timezone || "__inherit__"}
            onValueChange={(v) => setTimezone(v === "__inherit__" ? "" : v)}
          >
            <SelectTrigger id="timezone">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__inherit__">
                Inherit from workspace ({workspaceTimezone})
              </SelectItem>
              {TIMEZONE_OPTIONS.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <Button
          disabled={!dirty || update.isPending}
          onClick={async () => {
            try {
              await update.mutateAsync({
                locale,
                timezone: timezone || null,
              });
              toast.success("Preferences updated");
            } catch (e) {
              toast.error("Couldn't save", (e as Error).message);
            }
          }}
        >
          {update.isPending ? "Saving…" : "Save preferences"}
        </Button>
      </div>
    </SettingsSection>
  );
}
